import { useHistory, useLocation } from 'react-router-dom';
import { Box, styled } from '@mui/material';
import { rem } from '../../../components/panels/panelLayout';
import { InvertedSolidButton } from '../../../console/customers/dashboard/quick-actions/styles';
import { useActor } from '../../../components/hooks/useActor';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FEATURE_FLAG_KEYS } from '../../../config/growthbook';

const POOL_PATH_PREFIX = '/pool';

const ServiceBanner = () => {
  const history = useHistory();
  const location = useLocation();
  const actor = useActor();
  const crsEnable = useFeatureIsOn(FEATURE_FLAG_KEYS.CRS_ENABLE);

  // Check if current user is a pool tenant
  const isPoolTenant =
    actor.isPool || location.pathname.startsWith(POOL_PATH_PREFIX);

  const handleCtaButtonClick = () => {
    const catalogPath = isPoolTenant
      ? '/pool/resilience/catalog'
      : '/customer/resilience/catalog';

    history.push(catalogPath);
  };

  return (
    <StyledSmallContainer data-testid="services-banner">
      <StyledPlaceCenter>
        <StyledSmallBox>
          <img
            src="images/cowbell-resiliency-services-white-logo.svg"
            alt="Cowbell Resiliency Services"
          />
          <StyledSmallHeading>Now Offering</StyledSmallHeading>
          <StyledSmallHeading>Cowbell Resiliency Services</StyledSmallHeading>
          <StyledSmallSubHeading>
            Cowbell Resiliency Services helps policyholders detect and address
            vulnerabilities, strengthen infrastructure, and counter evolving
            threats.
          </StyledSmallSubHeading>
          {crsEnable && (
            <StyledCtaWrapper>
              <InvertedSolidButton onClick={handleCtaButtonClick}>
                Explore Services
              </InvertedSolidButton>
            </StyledCtaWrapper>
          )}
        </StyledSmallBox>
      </StyledPlaceCenter>
    </StyledSmallContainer>
  );
};

const StyledSmallContainer = styled('div')(() => ({
  background: `url(images/resilience-services-banner-bg-small.png) no-repeat center right / cover`,
  borderRadius: rem(5),
  height: '100%',
}));

const StyledPlaceCenter = styled('div')(() => ({
  display: 'flex',
  // Stretch, not centre: the content block fills the banner so its call to
  // action can sit on the bottom edge, level with the button in the tile
  // beside it, instead of floating in the middle.
  alignItems: 'stretch',
  height: '100%',
}));

const StyledSmallBox = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  // A flex column stretches its children by default, which blew the logo up to
  // the full width of the banner. It keeps its own size and sits left.
  '& img': {
    alignSelf: 'flex-start',
    // Narrower than the banner's old lockup, which tucked "RESILIENCY SERVICES"
    // under the wordmark as small caps. This one sets it full size on a second
    // line, so at the old width it matched the headings beneath and the block
    // read as three headings rather than a mark above two.
    maxWidth: rem(150),
    height: 'auto',
    // The headings carry only their own 4px rhythm, which left the logo sitting
    // directly on "Now Offering". It needs to read as a separate element.
    marginBottom: rem(14),
  },
  // 24 design px, the dashboard's card padding, so the banner's content lines
  // up with the tiles beside and below it instead of sitting further in.
  padding: rem(24),
}));

const StyledSmallHeading = styled('h1')(({ theme }) => ({
  // rem, not px: a hardcoded size stops scaling with the text around it once
  // the root font re-bases at wider viewports.
  fontSize: rem(24),
  fontWeight: 600,
  color: `${theme.palette.common.white} !important`,
  lineHeight: 1.2,
  margin: `${rem(4)} 0`,
}));

const StyledSmallSubHeading = styled('h2')(({ theme }) => ({
  fontSize: rem(13),
  fontWeight: 400,
  color: theme.palette.common.white,
  lineHeight: 1.55,
  margin: `${rem(10)} 0 0`,
  maxWidth: rem(420),
}));

const StyledCtaWrapper = styled('div')(() => ({
  // `auto` rather than a fixed gap: it takes up whatever height the banner has
  // spare, which is what puts the button on the bottom edge.
  marginTop: 'auto',
  paddingTop: rem(18),
}));

export default ServiceBanner;
